import { Avatar } from "@/components/CodeGen/Avatar";
import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { FREE_IN_BETA } from "@/constants";
import { useSettingsOpen } from "@/hooks";
import { cn } from "@/lib/utils";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useRouter } from "@tanstack/react-router";
import {
  Check,
  ChevronLeft,
  CreditCard,
  MoonStar,
  Orbit,
  Sun,
  User,
  X,
} from "lucide-react";
import { useState } from "react";
import { FreeInBetaTag } from "./CodeGen/FreeInBetaTag";

function AccountSettingsContent({
  page,
  setPage,
  feedbackEnabled,
  setFeedbackEnabled,
}: {
  page: "main" | "password";
  setPage: (page: "main" | "password") => void;
  feedbackEnabled: boolean;
  setFeedbackEnabled: (v: boolean) => void;
}) {
  const router = useRouter();
  const { user } = router.options.context.auth;

  if (page === "password") {
    return (
      <div className="flex flex-col h-full justify-start">
        <form className="w-full max-w-[672px]">
          <div className="pt-5 pb-4 flex flex-col gap-5">
            <div className="text-[18px] leading-[24px]">Password</div>
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="old-password"
                  className="block text-[14px] leading-[20px]"
                >
                  Old
                </label>
                <div className="text-xs text-muted-foreground">
                  Here comes a description
                </div>
              </div>
              <div>
                <Input
                  id="old-password"
                  type="password"
                  className="h-[40px] py-2 px-3"
                  placeholder="Old password"
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="new-password"
                  className="block text-[14px] leading-[20px]"
                >
                  New
                </label>
                <div className="text-xs text-muted-foreground">
                  Here comes a description
                </div>
              </div>
              <div>
                <Input
                  id="new-password"
                  type="password"
                  className="h-[40px] py-2 px-3"
                  placeholder="New password"
                />
              </div>
            </div>
          </div>
          <div className="flex flex-row gap-4">
            <Button
              variant="outline"
              type="button"
              className="h-[28px] py-0 px-3 text-[14px] leading-[20px]"
              onClick={() => setPage("main")}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              className="h-[28px] py-0 px-3 text-[14px] leading-[20px]"
            >
              Change password
            </Button>
          </div>
        </form>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-0 h-full justify-center">
      {/* Profile Section - pixel-perfect two-column layout */}
      <div className="text-[18px] pt-2 pb-6">Profile</div>
      <div className="grid grid-cols-[auto_1fr] gap-x-12 gap-y-6 items-center">
        {/* Profile Picture Row */}
        <div className="flex items-center gap-5">
          <div className="flex-shrink-0 h-[50px] w-[50px]">
            <Avatar sender="user" />
          </div>
          <div>
            <div className="text-[16px] leading-[24px]">Profile Picture</div>
            <div className="text-l leading-[20px] text-muted-foreground">
              Currently cannot change this we use your email account to set this
              currently
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          {/* No button for profile picture row */}
        </div>
        {/* Email Row */}
        <div className="flex flex-col gap-1">
          <div className="text-[16px] leading-[24px]">Email</div>
          <div className="text-l leading-[20px] text-muted-foreground">
            {user?.email || "-"}
          </div>
        </div>
        <div className="flex justify-end">
          <Button variant="outline" size="sm">
            Change email
          </Button>
        </div>
        {/* Password Row */}
        <div className="flex flex-col gap-1">
          <div className="text-[16px] leading-[24px]">Password</div>
          <div className="text-l leading-[20px] text-muted-foreground">
            Set a permanent password to login to your account.
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage("password")}
          >
            Add Password
          </Button>
        </div>
      </div>
      <hr className="my-8 border-muted" />
      {/* Theme Section */}
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <div className="text-[18px] leading-[24px]">Theme</div>
          <div className="text-l leading-[20px] text-muted-foreground">
            Customize the appearance of your application.
          </div>
        </div>
        <div className="flex items-center gap-8">
          <ThemeSwitcher />
        </div>
      </div>
      <hr className="my-8 border-muted" />
      {/* Feedback Toggle Section */}
      <div className="flex flex-col gap-1">
        <div className="text-[18px] leading-[24px]">Lorem ipsum dolor.</div>
        <div className="flex items-center justify-between">
          <div className="text-l leading-[20px] text-muted-foreground">
            Lorem ipsum dolor sit amet consectetur adipiscing elit quisque
            faucibus.
          </div>
          <Switch
            checked={feedbackEnabled}
            onCheckedChange={setFeedbackEnabled}
          />
        </div>
      </div>
    </div>
  );
}

function ThemeSwitcher() {
  const { theme, setTheme } = useTheme();
  const [selectedTheme, setSelectedTheme] = useState(theme);
  const options = [
    {
      value: "system",
      label: "System",
      Icon: Orbit,
    },
    {
      value: "light",
      label: "Light",
      Icon: Sun,
    },
    {
      value: "dark",
      label: "Dark",
      Icon: MoonStar,
    },
  ];
  return (
    <RadioGroup
      defaultValue={theme}
      onValueChange={(value) => {
        setTheme(value as "system" | "light" | "dark");
        setSelectedTheme(value as "system" | "light" | "dark");
      }}
      className="flex flex-row gap-[25px]"
    >
      {options.map(({ value, label, Icon }) => (
        <Label
          key={value}
          htmlFor={`theme-${value}`}
          className="flex flex-col gap-5 w-[75px] items-center cursor-pointer"
        >
          <span
            className={`text-l font-semibold ${selectedTheme === value ? "text-white" : "text-muted-foreground"}`}
          >
            {label}
          </span>
          <Icon
            className={`w-[30px] h-[30px] ${selectedTheme === value ? "text-white" : "text-muted-foreground/60"}`}
            strokeWidth={2.2}
          />
          <RadioGroupItem
            value={value}
            id={`theme-${value}`}
            className="h-4 w-4 border-muted-foreground data-[state=checked]:border-primary data-[state=checked]:bg-background"
          />
        </Label>
      ))}
    </RadioGroup>
  );
}

function SubscriptionSettingsPanel() {
  const [annual, setAnnual] = useState(false);

  const plans = [
    {
      name: "Free",
      price: "$XXX",
      period: "/ month",
      badge: (
        <span className="rounded-sm bg-[#3A3937] text-xs leading-s px-1 text-muted-foreground">
          Current plan
        </span>
      ),
      features: [
        "Unlimited API requests",
        "Unlimited API requests",
        "Unlimited API requests",
        "Unlimited API requests",
      ],
      button: (
        <Button
          variant="outline"
          size="sm"
          disabled
          className="h-[32px] py-0 px-3 gap-2"
        >
          Current plan
        </Button>
      ),
      highlight: false,
    },
    {
      name: "Pro",
      price: (
        <>
          <span className="text-muted-foreground">From</span> $XXX.00
        </>
      ),
      period: "/ month",
      badge: (
        <span className="rounded-sm bg-primary/15 text-xs leading-s px-1 text-primary">
          Popular
        </span>
      ),
      features: [
        "Unlimited API requests",
        "Unlimited API requests",
        "Unlimited API requests",
        "Unlimited API requests",
      ],
      button: (
        <Button
          variant="primary"
          size="sm"
          className="h-[32px] py-0 px-3 gap-2"
        >
          Upgrade to PRO
        </Button>
      ),
      highlight: true,
    },
    {
      name: "Team",
      price: (
        <>
          <span className="text-muted-foreground">From</span> $XXX.00
        </>
      ),
      period: "/ month",
      badge: null,
      features: [
        "Unlimited API requests",
        "Unlimited API requests",
        "Unlimited API requests",
        "Unlimited API requests",
      ],
      button: (
        <Button
          variant="primary"
          size="sm"
          className="h-[32px] py-0 px-3 gap-2"
        >
          Upgrade to Team
        </Button>
      ),
      highlight: false,
    },
  ];

  return (
    <div className="flex flex-col gap-4 h-full">
      <div className="flex items-center justify-between p-3">
        <div className="text-[16px] leading-[24px]">Annual billing</div>
        <Switch checked={annual} onCheckedChange={setAnnual} />
      </div>
      <div className="flex gap-3 w-full">
        {plans.map((plan) => (
          <div
            key={plan.name}
            className={`h-[400px] flex flex-col border rounded-xl px-8 py-5 w-1/3 ${plan.highlight ? "border-primary/70" : "border-border"}`}
          >
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-4">
                <span className="text-[16px] leading-[24px]">{plan.name}</span>
                {plan.badge}
              </div>
              <div className="flex gap-2 items-end">
                <div className="text-xl leading-[24px]">{plan.price}</div>
                <div className="text-xs leading-l text-muted-foreground">
                  {plan.period}
                </div>
              </div>
            </div>
            <hr className="my-4" />
            <ul className="flex flex-col gap-4">
              {plan.features.map((feature, index) => (
                <li
                  key={`${feature}-${
                    // biome-ignore lint/suspicious/noArrayIndexKey: temp
                    index
                  }`}
                  className="flex items-center gap-5 text-base text-foreground"
                >
                  <Check className="h-4 w-4 text-primary" />
                  <span className="text-[14px] leading-[20px]">{feature}</span>
                </li>
              ))}
            </ul>
            <div className="mt-auto flex flex-col gap-2">{plan.button}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SettingsModal() {
  const { settingsOpen, setSettingsOpen } = useSettingsOpen();
  const [tab, setTab] = useState<"account" | "subscription">("account");
  const [accountPage, setAccountPage] = useState<"main" | "password">("main");
  const [feedbackEnabled, setFeedbackEnabled] = useState(false);

  // Determine header and back button for current page
  let header = "";
  let showBack = false;
  if (tab === "account" && accountPage === "password") {
    header = "Back";
    showBack = true;
  } else if (tab === "account") {
    header = "Account";
  } else if (tab === "subscription") {
    header = "Subscription";
  }

  return (
    <DialogPrimitive.Root open={!!settingsOpen} onOpenChange={setSettingsOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 flex w-[1150px] h-[715px] -translate-x-1/2 -translate-y-1/2 bg-background shadow-lg rounded-lg gap-x-10",
          )}
        >
          {/* TODO */}
          <VisuallyHidden>
            <DialogPrimitive.Title>Settings</DialogPrimitive.Title>
            <DialogPrimitive.Description>Settings</DialogPrimitive.Description>
          </VisuallyHidden>
          {/* Sidebar - pixel-perfect adjustments only */}
          <nav className="w-[272px] flex flex-col border-r border-border bg-[#232323] h-full rounded-l-lg p-5 gap-5">
            <div className="text-xs text-muted-foreground pl-3">Settings</div>
            <div className="flex flex-col gap-2">
              <Button
                variant="ghost"
                className={cn(
                  "flex items-center gap-2 rounded-lg px-3 gap-3 text-s transition-all justify-start",
                  tab === "account"
                    ? "bg-[#353535] text-white"
                    : "bg-transparent text-[#E1E1E1] hover:bg-transparent hover:text-[#E1E1E1]",
                )}
                onClick={() => {
                  setTab("account");
                  setAccountPage("main");
                }}
              >
                <User
                  className={cn(
                    "w-5 h-5",
                    tab === "account" ? "text-white" : "text-[#E1E1E1]",
                  )}
                />
                <span>Account</span>
              </Button>
              <Button
                variant="ghost"
                className={cn(
                  "flex items-center gap-2 rounded-lg px-3 gap-3 text-s transition-all justify-start",
                  tab === "subscription"
                    ? "bg-[#353535] text-white"
                    : "bg-transparent text-[#E1E1E1] hover:bg-transparent hover:text-[#E1E1E1]",
                )}
                onClick={() => setTab("subscription")}
                disabled={FREE_IN_BETA}
              >
                <CreditCard
                  className={cn(
                    "w-5 h-5",
                    tab === "subscription" ? "text-white" : "text-[#E1E1E1]",
                  )}
                />
                <span>Subscription</span>
              </Button>
            </div>

            {FREE_IN_BETA && <FreeInBetaTag className="mt-auto" />}
          </nav>
          {/* Main content */}
          <div className="flex-1 h-full flex flex-col py-5 pr-10">
            {/* Header row */}
            <div className="flex items-center justify-between w-full py-3">
              <div className="flex items-center gap-2">
                {showBack && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setAccountPage("main")}
                    aria-label="Back to account settings"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </Button>
                )}
                <h2 className="text-[18px]">{header}</h2>
              </div>
              <Button
                variant="outline"
                size="icon"
                aria-label="Close"
                onClick={() => setSettingsOpen(false)}
              >
                <X className="h-6 w-6" />
              </Button>
            </div>
            <hr className="my-4 border-muted" />
            <div>
              {tab === "account" ? (
                <AccountSettingsContent
                  page={accountPage}
                  setPage={setAccountPage}
                  feedbackEnabled={feedbackEnabled}
                  setFeedbackEnabled={setFeedbackEnabled}
                />
              ) : (
                <SubscriptionSettingsPanel />
              )}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
